import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ChevronDown, Menu, X, Globe } from 'lucide-react';
import '@/App.css';

const LOGO_FULL = "/logo.png";

const productLinks = [
  { name: 'Unwanted Kit', href: '/products?filter=Unwanted Kit' },
  { name: 'Mifegymiso', href: '/products?filter=Mifegymiso' },
  { name: 'Gestapo', href: '/products?filter=Gestapo' },
  { name: 'Misoprostol', href: '/products?filter=Misoprostol' },
];

export default function Navbar() {
  const { t, lang, toggleLang } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [prodDropdown, setProdDropdown] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.about'), href: '/about' },
    { label: t('nav.gynecology'), href: '/gynecology' },
    { label: t('nav.abortion'), href: '/abortion' },
    { label: t('nav.blog'), href: '/blog' },
    { label: t('nav.contact'), href: '/contact' },
  ];

  return (
    <nav className="nav-glass sticky top-0 z-50 border-b border-border/60" data-testid="main-navbar">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo - uses mix-blend-mode to remove black background */}
          <Link to="/" className="flex-shrink-0" data-testid="navbar-logo-link">
            <img
              src={LOGO_FULL}
              alt="Dr. Preet Women's Clinic"
              className="logo-blend h-14 sm:h-14 md:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.slice(0, 1).map(link => (
              <Link key={link.href} to={link.href} data-testid={`nav-${link.href.slice(1) || 'home'}`}
                className={`px-3.5 py-2 text-sm font-medium rounded-full transition-all duration-200 ${isActive(link.href) ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:text-foreground hover:bg-muted'}`}>
                {link.label}
              </Link>
            ))}

            {/* Products dropdown */}
            <div className="relative" onMouseEnter={() => setProdDropdown(true)} onMouseLeave={() => setProdDropdown(false)}>
              <Link to="/products" data-testid="nav-products-dropdown"
                className={`px-3.5 py-2 text-sm font-medium rounded-full transition-all duration-200 flex items-center gap-1 ${location.pathname.startsWith('/products') ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:text-foreground hover:bg-muted'}`}>
                {t('nav.products')} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${prodDropdown ? 'rotate-180' : ''}`} />
              </Link>
              {prodDropdown && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-card border border-border rounded-2xl shadow-xl py-2 animate-fade-in" data-testid="products-dropdown-menu">
                  <Link to="/products" className="block px-4 py-2.5 text-sm text-foreground/80 hover:bg-muted hover:text-foreground transition-colors rounded-lg mx-1" data-testid="dropdown-all-products">
                    {lang === 'en' ? 'All Products' : 'Tous les produits'}
                  </Link>
                  <div className="h-px bg-border mx-3 my-1" />
                  {productLinks.map(p => (
                    <Link key={p.name} to={p.href} className="block px-4 py-2.5 text-sm text-foreground/80 hover:bg-muted hover:text-foreground transition-colors rounded-lg mx-1" data-testid={`dropdown-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
                      {p.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(1).map(link => (
              <Link key={link.href} to={link.href} data-testid={`nav-${link.href.slice(1)}`}
                className={`px-3.5 py-2 text-sm font-medium rounded-full transition-all duration-200 ${isActive(link.href) ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:text-foreground hover:bg-muted'}`}>
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right section */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={toggleLang} data-testid="language-toggle" className="flex items-center gap-1.5 text-sm font-medium rounded-full">
              <Globe className="w-4 h-4" /> {lang === 'en' ? 'FR' : 'EN'}
            </Button>
            <Link to="/contact" className="hidden md:block">
              <Button size="sm" className="rounded-full px-6 shadow-sm" data-testid="navbar-cta-btn">{t('hero.cta')}</Button>
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 animate-fade-in" data-testid="mobile-menu">
            {navLinks.map(link => (
              <Link key={link.href} to={link.href} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${link.href.slice(1) || 'home'}`}
                className={`block px-4 py-3 text-sm font-medium rounded-xl transition-colors ${isActive(link.href) ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:bg-muted'}`}>
                {link.label}
              </Link>
            ))}
            <Link to="/products" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-products"
              className={`block px-4 py-3 text-sm font-medium rounded-xl transition-colors ${location.pathname.startsWith('/products') ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:bg-muted'}`}>
              {t('nav.products')}
            </Link>
            {productLinks.map(p => (
              <Link key={p.name} to={p.href} onClick={() => setMobileOpen(false)} className="block px-8 py-2 text-sm text-muted-foreground hover:text-foreground">
                {p.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
